"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, Mail, MapPin, Phone, Send, ShieldCheck } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/50 rounded-lg border border-border">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="font-default text-xl font-bold text-foreground mb-2">
          Consultation Request Received
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Your message..." rows={4} className="resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium transition-colors">
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
            Sending Request...
          </span> : <span className="flex items-center justify-center gap-2">
            <Send className="size-4" />
            Request Consultation
          </span>}
      </Button>
    </Form>;
}
const highlights = [{
  icon: ShieldCheck,
  title: "Regulated Laboratory Expertise",
  desc: "Qualified analytical application specialists across Ireland and the UK."
}, {
  icon: Clock,
  title: "Rapid Technical Triage",
  desc: "Initial technical assessment and scoping call within 24 business hours."
}, {
  icon: MapPin,
  title: "On-Site & Virtual Delivery",
  desc: "Direct support from instrumentation qualification through lifecycle maintenance."
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border relative overflow-hidden">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left Column: Context & Information */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-8">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-secondary text-secondary-foreground uppercase tracking-wider mb-4 border border-border">
                Technical Advisory
              </span>
              <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight">
                Initiate Your Analytical Consultation
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Speak directly with our technical team about instrumentation selection, method integration, qualification protocols, or service contracts for your laboratory.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {highlights.map((item, index) => {
              const Icon = item.icon;
              return <div key={item.title} data-index={index} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-secondary/40 border border-border">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="size-4" />
                    </div>
                    <div>
                      <h3 className="font-default text-sm font-semibold text-foreground">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Direct Contact Channels
              </p>
              <div className="flex flex-col sm:flex-row gap-4 text-xs text-foreground">
                <a href="mailto:info@antech.ie" className="inline-flex items-center gap-2 hover:text-primary transition-colors">
                  <Mail className="size-3.5 text-primary" />
                  <span>info@antech.ie</span>
                </a>
                <a href="tel:+3531800268324" className="inline-flex items-center gap-2 hover:text-primary transition-colors">
                  <Phone className="size-3.5 text-primary" />
                  <span>+353 1 800 268 324</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Contact Form */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="border border-border shadow-md bg-card">
              <div className="px-6 pt-6 pb-2 border-b border-border/70">
                <h3 className="font-default text-xl font-bold text-foreground">
                  Contact Us - Antech Solutions
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Complete this form to schedule a technical consultation or request product qualification data.
                </p>
              </div>
              <CardContent className="pt-6">
                <FormProvider formId="contact_form" sectionName="consultation-cta" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                company_name: {
                  required: false
                },
                phone_number: {
                  required: false
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Request Consultation" successMessage="Thank you for reaching out. An Antech technical specialist will contact you promptly." errorMessage="Unable to submit your request at this moment. Please check your details and try again.">
                  <ConsultationFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}